/**
 * Chrome / Google page-translation detector.
 *
 * @covers whether Chrome's page-translate feature is active on top of the app's own locale,
 *         and the language pair it is translating — not covered by `$browser_language` alone.
 * @rule   Chrome marks a translated page by adding `translated-ltr` / `translated-rtl` to
 *         `<html>` and rewriting `<html lang>` to the target language; removing the translation
 *         drops the class again (`<html lang>` is restored separately, outside this module's
 *         control). Detection is strictly class-based: the app itself rewrites `<html lang>` on
 *         every locale change (main.js, App.vue's fetchRestaurantData), so a lang change with no
 *         translated class is normal operation and must never fire an event.
 * @why    Chrome's page translation rewrote order item names on a Portuguese tablet while every
 *         stored copy of the order (Uber payload, catalog, digital ticket) was correct — the
 *         `translate="no"` fix (v4.65.2) stops the rewrite on order data. This measures the
 *         other half: which UI locale the fleet actually runs, and how many tablets still have
 *         Chrome translating the page on top of it, and into what language — so the exposure is
 *         a number instead of a hunch.
 * @since  2026-10-16
 */
import posthog from 'posthog-js';
import Logger from '@/logger';

const logger = new Logger({ screen: 'translationTelemetry' });

const TRANSLATED_CLASSES = ['translated-ltr', 'translated-rtl'];

/** True when Chrome's translation widget has applied itself to `root`. */
function isPageTranslated(root) {
    return TRANSLATED_CLASSES.some((cls) => root.classList.contains(cls));
}

// Module-level: one observer for the whole page, same pattern as perfTelemetry's single LoAF
// observer. `lastKnown` is the state we last fired for, so a mutation batch that leaves the
// class unchanged (e.g. a lang-only rewrite, or the class set again) is a correctly silent no-op.
let observer = null;
let lastKnown = false;

/** The shared payload every transition sends — mirrors what `app_startup` already carries. */
function buildProps(getContext, root) {
    const ctx = (typeof getContext === 'function' ? getContext() : null) || {};
    return {
        from_locale: ctx.uiLocale ?? null,
        to_lang: root.getAttribute('lang') || null,
        browser_language: ctx.browserLanguage ?? null,
        restaurant: ctx.restaurant ?? null,
        client: ctx.client ?? null,
        deviceId: ctx.deviceId ?? null,
        appVersion: ctx.appVersion ?? null,
    };
}

function fireTransition(nowTranslated, getContext, root, ph) {
    lastKnown = nowTranslated;
    const props = buildProps(getContext, root);
    // Two independent telemetry calls, each guarded on its own: one failing must never
    // skip the other, and neither may ever throw into the caller (MutationObserver / startup).
    try {
        ph.capture(nowTranslated ? 'page_translated' : 'page_translation_reverted', props);
    } catch (_) { /* never let telemetry throw */ }
    try {
        ph.setPersonProperties({ page_translated: nowTranslated });
    } catch (_) { /* never let telemetry throw */ }
}

/**
 * Starts watching `root` for Chrome's translation markers. Call once, after PostHog is ready.
 * Idempotent: a second call before `stopTranslationTelemetry()` is a no-op (never attaches a
 * second observer). Also checks the current state immediately, in case Chrome had already
 * translated the page before this ran (e.g. applied during the pre-i18n boot race).
 *
 * @param {() => object} getContext - returns the live props for the next event:
 *   { uiLocale, browserLanguage, restaurant, client, deviceId, appVersion }.
 * @param {{ root?: HTMLElement, posthogInstance?: object }} [options]
 */
export function startTranslationTelemetry(getContext, options = {}) {
    try {
        const root = options.root ?? (typeof document !== 'undefined' ? document.documentElement : null);
        const ph = options.posthogInstance ?? posthog;
        if (observer || !root) return;

        lastKnown = isPageTranslated(root);
        if (lastKnown) {
            fireTransition(true, getContext, root, ph);
        }

        observer = new MutationObserver(() => {
            try {
                const nowTranslated = isPageTranslated(root);
                if (nowTranslated === lastKnown) return; // state unchanged -> no repeat
                fireTransition(nowTranslated, getContext, root, ph);
            } catch (error) {
                logger.console('translation mutation handler failed', error);
            }
        });
        observer.observe(root, { attributes: true, attributeFilter: ['class', 'lang'] });
    } catch (error) {
        logger.console('translation telemetry failed to start', error);
    }
}

/** Disconnects the observer and resets state. Safe to call even if never started. */
export function stopTranslationTelemetry() {
    try {
        if (observer) observer.disconnect();
    } catch (_) { /* ignore */ }
    observer = null;
    lastKnown = false;
}
